import type { Response } from '@/types/facebook'

type Method = 'get' | 'post' | 'delete'
interface Params {
  accessToken?: string
  fields?: string
  [key: string]: unknown
}

interface FBOptions {
  appId: string
  autoLogAppEvents: boolean
  xfbml: boolean
  version: string
}

interface CallbackResponse {
  first_name: string
  last_name: string
  email: string
}

type Callback = (response: CallbackResponse) => void
declare global {
  interface Window {
    fbAsyncInit: () => void
    FB: {
      init: (options: FBOptions) => void
      AppEvents: {
        logPageView: () => void
      }
      getLoginStatus: (
        callback: (response: Response) => void,
        force?: boolean,
      ) => void
      login: (
        callback: (response: Response) => void,
        params?: { scope?: string },
      ) => void
      api: (
        path: string,
        method?: Method | Params | Callback,
        params?: Params | Callback,
        callback?: Callback,
      ) => void
    }
  }
}

let sdkPromise: Promise<Window['FB']> | undefined
// the initialised SDK, once loaded: facebookLogin() opens the popup with it in the click itself
let sdk: Window['FB'] | undefined

/**
 * The Facebook SDK (login with Facebook, about 80 KB) is loaded on demand: by the components that offer the
 * Facebook button (login and sign-up modals, sign-up form, contest modals) when they are shown, so it is ready
 * by the click (the login popup must open right after the click, Safari blocks it otherwise), and awaited in
 * their click handlers. It used to be an async plugin: every page waited for Facebook before starting, and a
 * page view was logged to Facebook before any consent.
 */
export const loadFacebookSdk = (): Promise<Window['FB']> => {
  if (import.meta.server) {
    return Promise.reject(new Error('Facebook SDK: client only'))
  }
  sdkPromise ??= new Promise((resolve, reject) => {
    const { facebookId } = useRuntimeConfig().public

    window.fbAsyncInit = function onSDKInit() {
      window.FB.init({
        appId: facebookId,
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v8.0',
      })
      window.FB.AppEvents.logPageView()
      sdk = window.FB
      window.dispatchEvent(new Event('fb-sdk-ready'))
      resolve(window.FB)
    }

    const script = document.createElement('script')
    script.id = 'facebook-jssdk'
    script.src = 'https://connect.facebook.net/fr_FR/sdk.js'
    script.async = true
    script.crossOrigin = 'anonymous'
    script.onerror = () => {
      // a later click tries again
      sdkPromise = undefined
      script.remove()
      reject(new Error('Facebook SDK not loaded'))
    }
    document.head.appendChild(script)
  })
  return sdkPromise
}

// started when a component with the Facebook button is shown, so the SDK is there by the click
export const preloadFacebookSdk = () =>
  onMounted(() => {
    loadFacebookSdk().catch(() => {})
  })

/**
 * Opens the Facebook login popup and resolves with the access token, or undefined when the waper closes it or
 * refuses. FB.login runs in the click itself when the SDK is already there (preloadFacebookSdk): Safari and the
 * mobile browsers block a popup opened after an await or a network round trip, which the former
 * getLoginStatus-then-login sequence did. A waper who already allowed the app gets a popup that closes at once.
 */
export const facebookLogin = (): Promise<string | undefined> => {
  const open = (FB: Window['FB']) =>
    new Promise<string | undefined>((resolve) => {
      FB.login((response) => resolve(response.authResponse?.accessToken), {
        scope: 'email',
      })
    })

  return sdk ? open(sdk) : loadFacebookSdk().then(open)
}
